import imageCompression from 'browser-image-compression';
import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {MAX_IMG_SIZE} from '../../constants';
import Button from '../../incase-domain/components/button/Button';
import BaseFileLoader, {FileResult} from './BaseFileLoader';

interface ImageFileLoaderProps {
    onLoad: (files: FileResult[]) => void;
    multipleFiles?: boolean;
    child?: JSX.Element;
    hasExistingImage?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
}

function ImageFileLoader(props: ImageFileLoaderProps): ReactElement {
    const {onLoad, multipleFiles, child, hasExistingImage, inputRef} = props;
    const {t} = useTranslation();

    async function compress(imageFile: File): Promise<File> {
        if (imageFile.type === 'image/gif') {
            return imageFile;
        }

        const options = {
            maxSizeMB: MAX_IMG_SIZE,
            useWebWorker: true,
        };
        return imageCompression(imageFile, options);
    }

    const handleImageLoad = async (files: FileResult[]): Promise<void> => {
        try {
            const compressedFiles = await Promise.all(
                files.map(async (fileResult) => {
                    const compressedFile = await compress(fileResult.file);

                    const reader = new FileReader();
                    reader.readAsDataURL(compressedFile);

                    return new Promise<FileResult>((resolve) => {
                        reader.onload = () => {
                            resolve({
                                reader,
                                file: compressedFile,
                            });
                        };
                    });
                })
            );

            onLoad(compressedFiles);
        } catch (error) {
            onLoad(files);
        }
    };

    const getImageButton = (): JSX.Element => (
        <Button variant="soft" color="secondary" size="sm" fullWidth leading="ph:plus-bold">
            {hasExistingImage ? t('designSidepanel.replaceImage') : t('designSidepanel.addImage')}
        </Button>
    );

    return (
        <BaseFileLoader
            inputRef={inputRef}
            onLoad={handleImageLoad}
            type="image"
            accept="image/*"
            multipleFiles={multipleFiles}
            child={child || getImageButton()}
        />
    );
}

export default ImageFileLoader;
